import { forwardRef, InputHTMLAttributes, LegacyRef } from 'react';
import { cn } from '@/utils/classNames.ts';
import { InputProps } from 'react-daisyui';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

type Option<T> = {
  value: T;
  label: string;
};

type SelectInputProps<T> = { onChange?: (value: T) => void; options: Option<T>[] } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'color' | 'onChange' | 'value'
> &
  Pick<InputProps, 'size' | 'color' | 'bordered'> & {
    label?: string;
    error?: string;
    wrapperClassName?: string;
  } & { value: T };

const SelectInput = forwardRef(
  <T,>(
    {
      label,
      error,
      wrapperClassName,
      size = 'md',
      color,
      bordered = true,
      value,
      onChange,
      options,
      className,
      ...args
    }: SelectInputProps<T>,
    ref: LegacyRef<HTMLInputElement>
  ) => {
    const handleSelect = (value: T) => {
      if (onChange) {
        onChange(value);
      }
      // @ts-expect-error - blur() is a valid method
      document?.activeElement?.blur();
    };

    return (
      <div className={cn('form-control max-w-full w-full', wrapperClassName)}>
        {label ? (
          <label className="label">
            <span className="label-text">{label}</span>
          </label>
        ) : null}
        <label
          className={cn('input flex items-center justify-between gap-2 dropdown cursor-pointer max-w-full group', {
            'input-ghost': color === 'ghost',
            'input-primary': color === 'primary',
            'input-secondary': color === 'secondary',
            'input-accent': color === 'accent',
            'input-info': color === 'info',
            'input-success': color === 'success',
            'input-warning': color === 'warning',
            'input-error': error || color === 'error',
            'input-xs': size === 'xs',
            'input-sm': size === 'sm',
            'input-md': size === 'md' || size,
            'input-lg': size === 'lg',
            'input-bordered': bordered,
          })}
        >
          <input
            tabIndex={0}
            className={cn('grow cursor-pointer overflow-auto max-w-[calc(100%-1rem)]', className)}
            ref={ref}
            readOnly
            value={options.find((option) => option.value === value)?.label ?? ''}
            {...args}
          />
          <ul
            tabIndex={0}
            className="dropdown-content menu bg-white rounded-md z-10 p-1 shadow-lg border border-gray-200 w-full absolute left-0 top-[calc(100%+5px)] max-h-[200px] overflow-y-auto flex-nowrap hidden group-focus-within:flex"
          >
            {options.map((option) => (
              <li key={option.label}>
                <button
                  type="button"
                  className={cn(
                    'w-full text-left px-4 py-2 rounded transition-colors hover:bg-gray-100 active:bg-gray-200 focus:bg-gray-100',
                    {
                      'bg-gray-200 font-medium': option.value === value,
                    }
                  )}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
          <ChevronDownIcon className="w-4 shrink-0" />
        </label>
        {error ? (
          <label className="label">
            <span className="label-text-alt text-error">{error}</span>
          </label>
        ) : null}
      </div>
    );
  }
) as <T>(props: SelectInputProps<T> & { ref?: LegacyRef<HTMLInputElement> }) => JSX.Element;

export default SelectInput;
